import { useCallback, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IconButton } from 'src/components/Buttons';
import { MultiSelectionToggleGroup } from 'src/components/common/MultiSelect/Toggle/MultiSelectionToggleGroup';
import { TimeDistance } from 'src/components/common/TimeDistance';
import { AllocatePlaceholderEventsFlow } from 'src/components/Events/allocate-placeholder-events-flow';
import { EventItemHeader } from 'src/components/Events/EventItemHeader';
import {
  EventItemHeaderType,
  METRICS_PLACEHOLDER_DIMS,
} from 'src/components/Events/EventItemHeader/EventItemHeader';
import { EventItemHeaderV2 } from 'src/components/Events/EventItemHeader/EventItemHeaderV2';
import { InventoryEventActionDropdown } from 'src/components/Listings/InventoryActionDropdown';
import {
  MetricsDisplay,
  MetricsDisplayPlaceholder,
} from 'src/components/Metrics';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useEventLastReviewed } from 'src/hooks/useEventLastReviewed';
import { useGetEventFullInfo } from 'src/hooks/useGetEventFullInfo';
import { usePosLiteAccount } from 'src/hooks/usePosLiteAccount';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { createInventoryEventUrl } from 'src/navigations/Routes/InventoryEvent/InventoryEvent.utils';
import { MatchIcon } from 'src/svgs';
import { FullPageIcon } from 'src/svgs/FullPageIcon';
import { EventIdQueryParam } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { stringToUtcDate } from 'src/utils/dateTimeUtils';
import { getInventoryListingMetricsV2 } from 'src/utils/ticketMetricUtils';
import { Feature, ListingMetrics } from 'src/WebApiController';

import * as styles from './EventListingHeader.css';

export const EventListingHeader = (props: EventItemHeaderType) => {
  const { loginContext } = useAppContext();
  const location = useLocation();

  const { event, ...rest } = props;
  const { venue, performer } = useGetEventFullInfo(event);

  const hasConsignorMasterUI = useUserHasConsignorMasterUI();
  const isPosLite = usePosLiteAccount();

  const hasListingTableV2Feature = useUserHasFeature(Feature.ListingTableV2);

  const { eventLastReviewed } = useEventLastReviewed();
  const lastReviewedDateTime = event.virtId
    ? eventLastReviewed[event.virtId]
    : undefined;

  const getInventoryListingMetricsFn = getInventoryListingMetricsV2;

  const { eventMetrics } = useCatalogMetricsContext<ListingMetrics>();
  const metrics = useMemo(
    () =>
      getInventoryListingMetricsFn(
        eventMetrics?.[event.virtId],
        loginContext?.user?.activeAccount?.currencyCode
      ),
    [
      getInventoryListingMetricsFn,
      eventMetrics,
      event.virtId,
      loginContext?.user?.activeAccount?.currencyCode,
    ]
  );

  const {
    eventsExpansion: { expandedListItems, toggleListItemExpansion },
    refreshCatalog,
  } = useCatalogDataContext();

  const onToggleGroup = useCallback(
    (groupId: string) => {
      if (!expandedListItems.includes(groupId)) {
        toggleListItemExpansion([groupId]);
      }
    },
    [expandedListItems, toggleListItemExpansion]
  );

  const hasPlaceholderEventFeature = useUserHasFeature(
    Feature.PlaceholderEventGroup
  );
  // The backend sets `placeholders` only for synthesized placeholder events, so its presence
  // authoritatively identifies them — no need to infer from a null viagId/mpId, which also
  // covers events merely awaiting mapping.
  const isPlaceholderEvent =
    hasPlaceholderEventFeature &&
    event.viagId == null &&
    event.mpId === event.virtId;

  // "Allocate" this single placeholder event: open the allocation flow scoped to
  // just this event's slot (rather than routing to the whole group page). The
  // group's slots + PO are fetched lazily — only once the flow is opened — so the
  // inventory list doesn't fire a query per placeholder tile.
  const [allocateOpen, setAllocateOpen] = useState(false);

  const eventId = event.virtId;
  const searchParam = useMemo(() => {
    const searchParam = new URLSearchParams(location.search);
    searchParam.set(EventIdQueryParam, eventId);
    return searchParam;
  }, [eventId, location.search]);

  const actions = useMemo(() => {
    if (isPosLite) {
      return [
        <Link
          key="OpenEventPage"
          to={{
            pathname: createInventoryEventUrl(
              eventId,
              false // includeSearchParams
            ),
            search: searchParam.toString(),
          }}
          state={{
            returnUrl: `${location.pathname}${location.search}#${eventId}`,
          }}
        >
          <IconButton
            icon={
              <FullPageIcon
                fill={vars.color.textBrand}
                size={vars.iconSize.m}
                withHoverEffect
              />
            }
            titleContentId={ContentId.OpenEventPage}
          />
        </Link>,
      ];
    }
    return [
      ...(isPlaceholderEvent
        ? [
            // Allocate this single placeholder event via the selection modal.
            <IconButton
              key="AllocatePlaceholderEvent"
              onClick={() => setAllocateOpen(true)}
              icon={<MatchIcon fill={vars.color.textBrand} withHoverEffect />}
              titleContentId={ContentId.PlaceholderEventGroupAllocateEvent}
            />,
          ]
        : []),
      <Link
        key="OpenEventPage"
        to={{
          pathname: createInventoryEventUrl(
            eventId,
            false // includeSearchParams
          ),
          search: searchParam.toString(),
        }}
        state={{
          returnUrl: `${location.pathname}${location.search}#${eventId}`,
        }}
      >
        <IconButton
          icon={
            <FullPageIcon
              fill={vars.color.textBrand}
              size={vars.iconSize.m}
              withHoverEffect
            />
          }
          titleContentId={ContentId.OpenEventPage}
        />
      </Link>,
      <MultiSelectionToggleGroup
        key="MultiSelectionToggleGroup"
        groupId={props.event.virtId}
        onToggle={onToggleGroup}
      />,
      <InventoryEventActionDropdown
        key="InventoryEventActionDropdown"
        {...props}
      />,
    ];
  }, [
    eventId,
    isPlaceholderEvent,
    isPosLite,
    location.pathname,
    location.search,
    onToggleGroup,
    props,
    searchParam,
  ]);

  const header = hasListingTableV2Feature ? (
    <EventItemHeaderV2
      {...rest}
      event={event}
      performer={performer}
      venue={venue}
      showBoost
      headerMetrics={
        !hasConsignorMasterUI &&
        metrics && (
          <MetricsDisplay
            key="headermetrics"
            mainMetrics={
              <div className={styles.mainMetricsContainer}>
                {metrics.mainMetrics}
              </div>
            }
          />
        )
      }
      metrics={
        isPosLite ? null : hasConsignorMasterUI ? null : !metrics ? (
          <MetricsDisplayPlaceholder
            key="metrics-placeholder"
            {...METRICS_PLACEHOLDER_DIMS}
          />
        ) : (
          <Stack direction="column" gap="s" alignItems="end">
            <Stack gap="s" alignItems="center">
              <MetricsDisplay
                key="metrics"
                mainMetrics={null}
                subMetrics={metrics.subMetrics}
              />
            </Stack>
            {lastReviewedDateTime && (
              <span className={styles.lastReviewedText}>
                {'Reviewed '}
                <TimeDistance
                  date={stringToUtcDate(lastReviewedDateTime)}
                  frequency={60_000}
                />
              </span>
            )}
          </Stack>
        )
      }
      actions={actions}
      showMappingState={true}
      eventTags={metrics.tags}
    />
  ) : (
    <EventItemHeader
      {...rest}
      event={event}
      performer={performer}
      venue={venue}
      showBoost
      headerMetrics={
        !hasConsignorMasterUI &&
        metrics && (
          <MetricsDisplay
            key="headermetrics"
            mainMetrics={
              <div className={styles.mainMetricsContainer}>
                {metrics.mainMetrics}
              </div>
            }
          />
        )
      }
      metrics={
        isPosLite ? null : hasConsignorMasterUI ? null : !metrics ? (
          <MetricsDisplayPlaceholder
            key="metrics-placeholder"
            {...METRICS_PLACEHOLDER_DIMS}
          />
        ) : (
          <Stack direction="column" gap="s" alignItems="end">
            <Stack gap="s" alignItems="center">
              <MetricsDisplay
                key="metrics"
                mainMetrics={null}
                subMetrics={metrics.subMetrics}
              />
            </Stack>
            {lastReviewedDateTime && (
              <span className={styles.lastReviewedText}>
                {'Reviewed '}
                <TimeDistance
                  date={stringToUtcDate(lastReviewedDateTime)}
                  frequency={60_000}
                />
              </span>
            )}
          </Stack>
        )
      }
      eventTags={metrics.tags}
      actions={actions}
      showMappingState={true}
    />
  );

  return (
    <>
      {header}
      {isPlaceholderEvent && event.posId && (
        <AllocatePlaceholderEventsFlow
          isOpen={allocateOpen}
          onClose={() => setAllocateOpen(false)}
          onAllocationDone={() => {
            setAllocateOpen(false);
            refreshCatalog?.();
          }}
          posEventIdsToAllocate={[event.posId]}
          venue={venue}
          performer={performer}
        />
      )}
    </>
  );
};
